import { makeStyles } from '@mui/styles';
import { Grid, Tooltip, Typography } from 'apollo-react';
import * as colors from 'apollo-react/colors';
import Modal from 'apollo-react/components/Modal';
import TextField from 'apollo-react/components/TextField';
import { shadowLevel1, shadowLevel3 } from 'apollo-react/shadows';
import * as icons from 'apollo-react-icons';
import Close from 'apollo-react-icons/Close';
import Search from 'apollo-react-icons/Search';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';

import { useTranslation } from '../../../../custom-hooks/useTranslation';

const useStyles = makeStyles({
  modal: {
    height: '70%',
    width: '60%',
    minWidth: 650,
    maxWidth: 1200,
  },
  search: {
    width: 300,
    marginTop: 0,
  },
  card: {
    padding: '8px 0 16px 0',
    textAlign: 'center',
    boxShadow: shadowLevel1,
    border: `1px solid ${colors.neutral3}`,
    borderRadius: 4,
    cursor: 'pointer',
    '&:hover': {
      borderColor: colors.neutral4,
      boxShadow: shadowLevel3,
    },
  },
  selectedCard: {
    backgroundColor: colors.neutral3,
  },
  iconName: {
    fontSize: 12,
  },
  grid: {
    minWidth: 160,
  },
  select: {
    marginRight: 16,
    width: 144,
  },
  iconContainer: {
    height: 48,
    width: 35,
    margin: 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  noneContainer: {
    height: 66,
    margin: 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

const noneIconName = 'None';

const IconsModal = ({ handleClose, confirmIconSelect, showNoneItem = false }) => {
  const classes = useStyles();
  const translateString = useTranslation();
  const searchInput = useRef();

  const [search, setSearch] = useState('');
  const [selectedIcon, setSelectedIcon] = useState();

  const allIconNames = showNoneItem ? { None: noneIconName, ...icons } : icons;

  useEffect(() => {
    setTimeout(function() {
      searchInput?.current?.focus();
    }, 100);
  }, []);

  const handleSelectIcon = (iconName) => {
    setSelectedIcon(iconName);
  };

  const handleSearch = ({ target: { value } }) => {
    setSearch(value?.trim());
  };

  const handleConfirmIconSelect = (iconName) => {
    confirmIconSelect(noneIconName === iconName ? '' : iconName);
    handleClose();
  };

  const clearSearchText = () => {
    setSearch('');
    searchInput.current.focus();
  };

  return (
    <Modal
      open
      variant="default"
      onClose={handleClose}
      title={translateString('Icons')}
      buttonProps={[
        {
          label: translateString('Cancel'),
        },
        {
          label: translateString('Select'),
          onClick: () => handleConfirmIconSelect(selectedIcon),
          disabled: !selectedIcon,
        },
      ]}
      className={classes.modal}
      disableBackdropClick={false}
    >
      <Grid item>
        <TextField
          className={classes.search}
          startIcon={
            <Tooltip title={translateString('Search')} placement="bottom">
              <Search />
            </Tooltip>
          }
          type="search"
          inputRef={searchInput}
          placeholder={translateString('Search')}
          icon={search && <Close className={classes.clearIcon} />}
          iconProps={{ onClick: clearSearchText }}
          value={search}
          onChange={handleSearch}
        />
      </Grid>
      <Grid container spacing={1} wrap="wrap" style={{ marginBottom: -8 }}>
        {Object.keys(allIconNames)
          .filter((iconName) => {
            if (!search) {
              return true;
            }

            return iconName.toLowerCase().includes(search.toLowerCase());
          })
          .map((iconName) => {
            const Icon = icons[iconName];
            let content;

            if (iconName === noneIconName) {
              content = <div className={classes.noneContainer}>{noneIconName}</div>;
            } else {
              content = (
                <>
                  <div className={classes.iconContainer}>
                    <Icon />
                  </div>
                  <Typography className={classes.iconName}>{iconName}</Typography>
                </>
              );
            }

            return (
              <Grid key={iconName} item xs={2} className={classes.grid}>
                <div
                  className={classNames(
                    classes.card,
                    iconName === selectedIcon ? classes.selectedCard : null
                  )}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleSelectIcon(iconName)}
                  onDoubleClick={() => handleConfirmIconSelect(iconName)}
                  onKeyDown={(e) => {
                    if (iconName && e.key === 'Enter') {
                      handleConfirmIconSelect(iconName);
                    }
                  }}
                >
                  {content}
                </div>
              </Grid>
            );
          })}
      </Grid>
    </Modal>
  );
};

export default IconsModal;
